import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import type { UserCommunication } from '@this+that/models';

import { planCommunicationDeletion } from './planCommunicationDeletion';
import { useBulkDeleteDrafts } from './useBulkDeleteDrafts';
import { useDiscardLocalDraft } from './useDiscardLocalDraft';
import { toLocalDraftSelectionId } from '../../../utils/localDraftSelection';
import { useCustomToast } from '../../useCustomToast';

export interface DeleteOthersToastKeys {
  loading: string;
  success: string;
  error: string;
}

export interface DeleteCommunicationsParams {
  messageIds: string[];
  communications: UserCommunication[];
  deleteOthers: (ids: string[], onCleared?: () => void) => Promise<unknown>;
  othersToast?: DeleteOthersToastKeys;
  onCleared?: () => void;
}

export const DELETE_TOAST_KEYS: DeleteOthersToastKeys = {
  loading: 'inbox.userCommunication.actions.deleting',
  success: 'inbox.userCommunication.actions.deleteMultipleSuccess',
  error: 'inbox.userCommunication.actions.deleteError',
};

export const TRASH_TOAST_KEYS: DeleteOthersToastKeys = {
  loading: 'inbox.userCommunication.actions.trashing',
  success: 'inbox.userCommunication.actions.trashMultipleSuccess',
  error: 'inbox.userCommunication.actions.trashError',
};

export const useDeleteCommunications = () => {
  const { t } = useTranslation();
  const { promiseToast } = useCustomToast();
  const { handleBulkDeleteDrafts } = useBulkDeleteDrafts();
  const discardLocalDraft = useDiscardLocalDraft();

  return useCallback(
    async ({
      messageIds,
      communications,
      deleteOthers,
      othersToast,
      onCleared,
    }: DeleteCommunicationsParams) => {
      const { serverDraftMessageIds, localSessionIds, otherIds } =
        planCommunicationDeletion(messageIds, communications);
      const draftTargets = [
        ...serverDraftMessageIds,
        ...localSessionIds.map(toLocalDraftSelectionId),
      ];

      if (draftTargets.length === 0) {
        const promise = deleteOthers(otherIds, onCleared);
        if (othersToast) {
          promiseToast(
            promise,
            t(othersToast.loading),
            t(othersToast.success, { count: otherIds.length }),
            t(othersToast.error),
          );
        }
        return promise;
      }

      if (otherIds.length === 0) {
        const [onlySessionId] = localSessionIds;
        if (messageIds.length === 1 && onlySessionId) {
          onCleared?.();
          return discardLocalDraft(onlySessionId);
        }
        return handleBulkDeleteDrafts(draftTargets, onCleared);
      }

      onCleared?.();
      const promise = Promise.all([
        handleBulkDeleteDrafts(draftTargets, undefined, { silent: true }),
        deleteOthers(otherIds),
      ]);
      promiseToast(
        promise,
        t(DELETE_TOAST_KEYS.loading),
        t(DELETE_TOAST_KEYS.success, { count: messageIds.length }),
        t(DELETE_TOAST_KEYS.error),
      );
      return promise;
    },
    [handleBulkDeleteDrafts, discardLocalDraft, promiseToast, t],
  );
};
